import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import trLocale from 'date-fns/locale/tr';
import enLocale from 'date-fns/locale/en-US';
import { useTranslation } from 'react-i18next';
import ProducibleProductAutocomplete from '../shared/ProducibleProductAutocomplete';
import type { ProducibleProduct, ProductionWarehouse } from '../../types/production.types';
import { formatQty, formatQuantity, isFutureCalendarDate } from '../../utils/productionFormat';
import { parseTrQuantity } from '../../../../utils/numberFormat';
import type { ProductionQuantityMode } from '../../hooks/useProductionOrderForm';

interface ProductionHeaderFormProps {
  disabled?: boolean;
  product: ProducibleProduct | null;
  onProductChange: (product: ProducibleProduct | null) => void;
  plannedQuantity: number | null;
  onPlannedQuantityChange: (value: number | null) => void;
  /** Ana malzemeye göre üretim (reçetede ana malzeme varsa). */
  quantityMode?: ProductionQuantityMode;
  onQuantityModeChange?: (mode: ProductionQuantityMode) => void;
  baseQuantity?: number | null;
  onBaseQuantityChange?: (value: number | null) => void;
  warehouses: ProductionWarehouse[];
  inputWarehouseId: string;
  onInputWarehouseChange: (id: string) => void;
  outputWarehouseId: string;
  onOutputWarehouseChange: (id: string) => void;
  separateOutput: boolean;
  onSeparateOutputChange: (value: boolean) => void;
  productionDate: Date | null;
  onProductionDateChange: (value: Date | null) => void;
  notes: string;
  onNotesChange: (value: string) => void;
  initialProductId?: string | null;
  onInitialProduct?: (product: ProducibleProduct) => void;
}

function parseQuantity(raw: string): number | null {
  const normalized = raw.trim().replace(',', '.');
  if (!normalized) return null;
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}

/** Ana malzeme miktarı: Türkçe yazım ("5,5", "1.250,5") desteklenir; metin yazarken korunur. */
const BaseQuantityField: React.FC<{
  label: string;
  unit?: string | null;
  value: number | null;
  disabled?: boolean;
  helperText: string;
  onChange: (value: number | null) => void;
}> = ({ label, unit, value, disabled, helperText, onChange }) => {
  const [text, setText] = useState(value != null ? formatQty(value) : '');

  useEffect(() => {
    const parsed = parseTrQuantity(text);
    const same = value == null ? text.trim() === '' || Number.isNaN(parsed) : parsed === value;
    if (!same) setText(value != null ? formatQty(value) : '');
    // Yalnız dışarıdan gelen değer değişince metin yenilenir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const parsed = parseTrQuantity(text);
  const invalid = text.trim() !== '' && (Number.isNaN(parsed) || parsed <= 0);

  return (
    <TextField
      fullWidth
      required
      size="small"
      label={label}
      value={text}
      disabled={disabled}
      error={invalid}
      onChange={(event) => {
        const next = event.target.value;
        setText(next);
        const number = parseTrQuantity(next);
        onChange(next.trim() === '' || Number.isNaN(number) ? null : number);
      }}
      inputProps={{ inputMode: 'decimal' }}
      InputProps={{
        endAdornment: unit ? <InputAdornment position="end">{unit}</InputAdornment> : undefined,
      }}
      helperText={helperText}
    />
  );
};

const ProductionHeaderForm: React.FC<ProductionHeaderFormProps> = ({
  disabled,
  product,
  onProductChange,
  plannedQuantity,
  onPlannedQuantityChange,
  quantityMode = 'total',
  onQuantityModeChange,
  baseQuantity = null,
  onBaseQuantityChange,
  warehouses,
  inputWarehouseId,
  onInputWarehouseChange,
  outputWarehouseId,
  onOutputWarehouseChange,
  separateOutput,
  onSeparateOutputChange,
  productionDate,
  onProductionDateChange,
  notes,
  onNotesChange,
  initialProductId,
  onInitialProduct,
}) => {
  const { t, i18n } = useTranslation('production');
  const dateLocale = i18n.language === 'en' ? enLocale : trLocale;
  const future = isFutureCalendarDate(productionDate);
  const baseName = product?.baseIngredientName || '';
  const canUseBase = Boolean(product?.baseIngredientId && onQuantityModeChange && onBaseQuantityChange);
  const byBase = canUseBase && quantityMode === 'base';
  const batchHint = product && product.baseIngredientBatchQuantity != null && product.recipeOutputQuantity > 0
    ? t('editor.baseBatchHint', {
      quantity: formatQty(product.baseIngredientBatchQuantity),
      unit: product.baseIngredientUnit || '',
      name: baseName,
      output: formatQuantity(product.recipeOutputQuantity),
      outputUnit: product.unit,
    })
    : ' ';

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ProducibleProductAutocomplete
            value={product}
            onChange={onProductChange}
            disabled={disabled}
            initialProductId={initialProductId}
            onInitialProduct={onInitialProduct}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          {canUseBase && (
            <RadioGroup
              row
              value={byBase ? 'base' : 'total'}
              onChange={(event) => onQuantityModeChange?.(event.target.value as ProductionQuantityMode)}
              sx={{ mt: -1, mb: 0.5 }}
            >
              <FormControlLabel
                value="total"
                control={<Radio size="small" />}
                disabled={disabled}
                label={<Typography variant="body2">{t('editor.modeTotal')}</Typography>}
              />
              <FormControlLabel
                value="base"
                control={<Radio size="small" />}
                disabled={disabled}
                label={<Typography variant="body2">{t('editor.modeBase', { name: baseName })}</Typography>}
              />
            </RadioGroup>
          )}
          {byBase ? (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
              <BaseQuantityField
                label={t('editor.baseQuantity', { name: baseName })}
                unit={product?.baseIngredientUnit}
                value={baseQuantity}
                disabled={disabled}
                helperText={batchHint}
                onChange={(value) => onBaseQuantityChange?.(value)}
              />
              <TextField
                fullWidth
                size="small"
                label={t('editor.plannedCalculated')}
                value={plannedQuantity != null ? formatQty(plannedQuantity) : ''}
                disabled={disabled}
                InputProps={{
                  readOnly: true,
                  endAdornment: product?.unit ? <InputAdornment position="end">{product.unit}</InputAdornment> : undefined,
                }}
                helperText={plannedQuantity != null && plannedQuantity > 0
                  ? t('editor.basePlannedHint', {
                    quantity: formatQty(plannedQuantity),
                    unit: product?.unit || '',
                    product: product?.name || '',
                  })
                  : t('editor.basePlannedPending')}
              />
            </Box>
          ) : (
            <TextField
              fullWidth
              required
              size="small"
              type="number"
              label={t('editor.plannedQuantity')}
              value={plannedQuantity ?? ''}
              disabled={disabled}
              onChange={(event) => onPlannedQuantityChange(parseQuantity(event.target.value))}
              inputProps={{ min: 0, step: 0.001 }}
              InputProps={{
                endAdornment: product?.unit ? <InputAdornment position="end">{product.unit}</InputAdornment> : undefined,
              }}
              helperText={product && product.recipeOutputQuantity > 0
                ? t('editor.recipeHint', {
                  quantity: formatQuantity(product.recipeOutputQuantity),
                  unit: product.unit,
                })
                : ' '}
            />
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small" required disabled={disabled}>
            <InputLabel>{t('editor.inputWarehouse')}</InputLabel>
            <Select
              label={t('editor.inputWarehouse')}
              value={inputWarehouseId}
              onChange={(event) => onInputWarehouseChange(event.target.value)}
            >
              {warehouses.map((warehouse) => (
                <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small" required disabled={disabled || !separateOutput}>
            <InputLabel>{t('editor.outputWarehouse')}</InputLabel>
            <Select
              label={t('editor.outputWarehouse')}
              value={outputWarehouseId}
              onChange={(event) => onOutputWarehouseChange(event.target.value)}
            >
              {warehouses.map((warehouse) => (
                <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={separateOutput}
                disabled={disabled}
                onChange={(event) => onSeparateOutputChange(event.target.checked)}
              />
            )}
            label={t('editor.separateOutput')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <DatePicker
            label={t('editor.productionDate')}
            value={productionDate}
            disabled={disabled}
            onChange={onProductionDateChange}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                helperText: future ? t('editor.futureDate') : ' ',
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label={t('editor.notes')}
            value={notes}
            disabled={disabled}
            onChange={(event) => onNotesChange(event.target.value.slice(0, 500))}
            inputProps={{ maxLength: 500 }}
          />
          <Typography variant="caption" color="text.secondary">{notes.length}/500</Typography>
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};

export default ProductionHeaderForm;
